import { buildClickMap } from "./events.js";
export function deriveCounters(events) {
    const raw = (type) => events.filter((e) => e.type === type).length;
    const uniq = (type) => new Set(events.filter((e) => e.type === type).map((e) => e.subscriberId)).size;
    return {
        sent: raw("sent"),
        delivered: raw("delivered"),
        opens: uniq("open"),
        clicks: uniq("click"),
        bounces: raw("bounce"),
        complaints: raw("complaint"),
        unsubscribes: raw("unsubscribe"),
        rejects: raw("reject"),
        renderingFailures: raw("rendering_failure"),
        // Raw, not unique: the same message can be delayed several times, and each
        // one is a separate signal about the receiver. Collapsing them would hide a
        // receiver deferring the whole campaign repeatedly.
        deliveryDelays: raw("delivery_delay"),
    };
}
export function deliverabilityRates(c) {
    const delivered = c.delivered > 0 ? c.delivered : 1;
    const sent = c.sent > 0 ? c.sent : 1;
    return {
        openRate: c.opens / delivered,
        clickRate: c.clicks / delivered,
        bounceRate: c.bounces / sent,
        complaintRate: c.complaints / sent,
    };
}
/**
 * The average of each send's OWN open and click rate (#369).
 *
 * The product owner's call on #369: an "average open rate" across several sends
 * is the mean of per-send opens ÷ delivered, not summed opens ÷ summed
 * delivered. A send with nothing delivered has no rate and is left out rather
 * than averaged in as 0%. `undefined` when no send in the set has a rate.
 *
 * Shared by the series report route and the Report screen's series totals so
 * the two cannot drift apart.
 */
export function meanSendRates(sends) {
    const rated = sends.filter((s) => s.delivered > 0);
    if (rated.length === 0)
        return undefined;
    const mean = (f) => rated.reduce((sum, s) => sum + f(s), 0) / rated.length;
    return {
        openRate: mean((s) => s.opens / s.delivered),
        clickRate: mean((s) => s.clicks / s.delivered),
    };
}
/** Build the full hot-path report for one campaign (counters + rates + click map). */
export async function buildCampaignReport(stores, orgId, campaignId) {
    // ONE read of the event log, not two (#182). This read the whole log to derive
    // counters and then called `buildClickMap`, which read it again — two full
    // reads of an unbounded item set to render one screen.
    const events = await stores.events.all(orgId, campaignId);
    // Prefer the STORED counters. They are maintained transactionally with each
    // append (#221) and are what `checkDeliverability` evaluates, so using them
    // here means the bounce rate an operator reads is the same number that halted
    // the campaign — deriving separately let the report and the halt disagree.
    const campaign = await stores.campaigns.get(orgId, campaignId);
    const counters = campaign?.counters ?? deriveCounters(events);
    const clickMap = await buildClickMap(stores, orgId, campaignId, events);
    return { orgId, campaignId, counters, rates: deliverabilityRates(counters), clickMap };
}
//# sourceMappingURL=reporting.js.map